export {
  normalizeChatGptMathDelimiters,
  splitMathSegments,
} from '@/lib/markdown/math-delimiters'
export type { MathSplit } from '@/lib/markdown/math-delimiters'

export type MathImage = {
  dataUrl: string
  width: number
  height: number
  baseline?: number
  descent?: number
}

type MathRenderer = { adaptor: any; html: any }

let mathRendererPromise: Promise<MathRenderer> | null = null
let katexModulePromise: Promise<any> | null = null

const DEFAULT_IMG_PX = Math.round(6.5 * 96)
const MATH_EXPORT_SCALE = 2

const getMathRenderer = async (): Promise<MathRenderer> => {
  if (!mathRendererPromise) {
    mathRendererPromise = (async () => {
      const [{ mathjax }, { TeX }, { AllPackages }] = await Promise.all([
        import('mathjax-full/js/mathjax.js'),
        import('mathjax-full/js/input/tex.js'),
        import('mathjax-full/js/input/tex/AllPackages.js'),
      ])
      const [{ SVG }] = await Promise.all([
        import('mathjax-full/js/output/svg.js'),
      ])
      const [{ liteAdaptor }] = await Promise.all([
        import('mathjax-full/js/adaptors/liteAdaptor.js'),
      ])
      const [{ RegisterHTMLHandler }] = await Promise.all([
        import('mathjax-full/js/handlers/html.js'),
      ])

      const adaptor = liteAdaptor()
      RegisterHTMLHandler(adaptor)
      const tex = new TeX({
        packages: AllPackages,
        inlineMath: [
          ['$', '$'],
          ['\\(', '\\)'],
        ],
        displayMath: [
          ['$$', '$$'],
          ['\\[', '\\]'],
        ],
      })
      const svg = new SVG({ fontCache: 'none' })
      const html = mathjax.document('', { InputJax: tex, OutputJax: svg })
      return { adaptor, html }
    })()
  }
  return mathRendererPromise
}

const loadKatex = async () => {
  if (!katexModulePromise) {
    katexModulePromise = import('katex').then((mod) => {
      const maybeDefault = (mod as any).default
      if (maybeDefault && typeof maybeDefault.renderToString === 'function') {
        return maybeDefault
      }
      return mod as any
    })
  }
  return katexModulePromise
}

const svgToPng = (
  svgContent: string,
  widthHint?: number,
  heightHint?: number,
  baselineRatio?: number,
) =>
  new Promise<MathImage | null>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null)
      return
    }
    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const naturalWidth = img.width || 0
      const naturalHeight = img.height || 0
      const measuredWidth = naturalWidth || widthHint || DEFAULT_IMG_PX
      const measuredHeight =
        naturalHeight || heightHint || Math.round(measuredWidth * 0.6)
      const targetWidth = Math.max(1, Math.round(measuredWidth))
      const scale = targetWidth / Math.max(naturalWidth || measuredWidth, 1)
      const targetHeight = Math.max(
        1,
        Math.round(measuredHeight * (naturalHeight ? scale : 1)),
      )
      const canvas = document.createElement('canvas')
      const exportWidth = Math.max(
        1,
        Math.round(targetWidth * MATH_EXPORT_SCALE),
      )
      const exportHeight = Math.max(
        1,
        Math.round(targetHeight * MATH_EXPORT_SCALE),
      )
      canvas.width = exportWidth
      canvas.height = exportHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(url)
        resolve(null)
        return
      }
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, exportWidth, exportHeight)
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.setTransform(MATH_EXPORT_SCALE, 0, 0, MATH_EXPORT_SCALE, 0, 0)
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
      let dataUrl: string | null = null
      try {
        dataUrl = canvas.toDataURL('image/png')
      } catch (err) {
        console.warn('Math canvas export failed', err)
        dataUrl = null
      }
      URL.revokeObjectURL(url)
      if (!dataUrl) {
        resolve(null)
        return
      }
      const baseline =
        typeof baselineRatio === 'number'
          ? Math.max(0, Math.round(targetHeight * baselineRatio))
          : undefined
      resolve({
        dataUrl,
        width: targetWidth,
        height: targetHeight,
        baseline,
        descent:
          baseline !== undefined
            ? Math.max(targetHeight - baseline, 0)
            : undefined,
      })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(null)
    }
    img.src = url
  })

const extractViewBox = (
  svgContent: string,
): { x: number; y: number; width: number; height: number } | null => {
  const viewBoxMatch = svgContent.match(/viewBox="([^"]+)"/i)
  if (!viewBoxMatch) return null
  const parts = viewBoxMatch[1]
    .trim()
    .split(/\s+/)
    .map((p) => Number.parseFloat(p))
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null
  const [x, y, width, height] = parts
  return { x, y, width, height }
}

const measureSvg = (svgContent: string) => {
  if (typeof window === 'undefined') return null
  const wrapper = document.createElement('div')
  wrapper.style.position = 'absolute'
  wrapper.style.visibility = 'hidden'
  wrapper.style.pointerEvents = 'none'
  wrapper.style.left = '-9999px'
  wrapper.style.top = '-9999px'
  wrapper.innerHTML = svgContent
  const svgEl = wrapper.querySelector('svg')
  document.body.appendChild(wrapper)
  const rect = svgEl?.getBoundingClientRect()
  const width = Math.max(
    1,
    Math.round(rect?.width || rect?.right || DEFAULT_IMG_PX),
  )
  const height = Math.max(
    1,
    Math.round(rect?.height || rect?.bottom || width * 0.6),
  )
  wrapper.remove()
  return { width, height }
}

const setSvgExplicitSize = (
  svgContent: string,
  size?: { width: number; height: number },
) => {
  if (!size) return svgContent
  let updated = svgContent
  if (/width="[^"]*"/i.test(updated)) {
    updated = updated.replace(/width="[^"]*"/i, `width="${size.width}"`)
  } else {
    updated = updated.replace(/<svg\b/i, `<svg width="${size.width}"`)
  }
  if (/height="[^"]*"/i.test(updated)) {
    updated = updated.replace(/height="[^"]*"/i, `height="${size.height}"`)
  } else {
    updated = updated.replace(/<svg\b/i, `<svg height="${size.height}"`)
  }
  return updated
}

const renderLatexWithKatex = async (
  latex: string,
  displayMode: boolean,
): Promise<MathImage | null> => {
  if (typeof window === 'undefined') return null
  try {
    const katex = await loadKatex()
    if (!katex?.renderToString) return null

    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.visibility = 'hidden'
    container.style.pointerEvents = 'none'
    container.style.left = '-9999px'
    container.style.top = '-9999px'
    container.innerHTML = katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      output: 'html',
    })

    document.body.appendChild(container)
    const rect = container.getBoundingClientRect()
    const measuredWidth = Math.max(
      1,
      Math.round(rect.width || DEFAULT_IMG_PX * 0.4),
    )
    const measuredHeight = Math.max(
      1,
      Math.round(rect.height || measuredWidth * 0.6),
    )

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${measuredWidth}" height="${measuredHeight}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:#ffffff;color:#000000">${container.innerHTML}</div></foreignObject></svg>`

    document.body.removeChild(container)
    const estimatedBaselineRatio = displayMode ? 0.75 : 0.82
    return svgToPng(svg, measuredWidth, measuredHeight, estimatedBaselineRatio)
  } catch (err) {
    console.warn('KaTeX render failed', err)
    return null
  }
}

export const renderLatexToPng = async (
  latex: string,
  displayMode: boolean,
): Promise<MathImage | null> => {
  if (typeof window === 'undefined') return null
  const trimmed = latex.trim()
  if (!trimmed) return null
  try {
    const { adaptor, html } = await getMathRenderer()
    const node = html.convert(trimmed, { display: displayMode })
    const outerSvg = adaptor.outerHTML(node) as string
    if (outerSvg.includes('data-mjx-error')) {
      throw new Error('MathJax render produced an error node')
    }
    const svgMatch = outerSvg.match(/<svg[\s\S]*<\/svg>/i)
    const svg = svgMatch?.[0] ?? outerSvg
    const viewBox = extractViewBox(svg)
    const measuredSize = measureSvg(svg)
    const sizedSvg = setSvgExplicitSize(svg, measuredSize || undefined)
    const baselineRatio =
      viewBox && viewBox.height !== 0
        ? Math.max(
            0,
            Math.min(1, Math.abs(viewBox.y) / Math.abs(viewBox.height)),
          )
        : undefined
    const png = await svgToPng(
      sizedSvg,
      measuredSize?.width ?? viewBox?.width,
      measuredSize?.height ?? viewBox?.height,
      baselineRatio,
    )
    if (png) return png
  } catch (err) {
    console.warn('MathJax render failed', err)
  }

  return renderLatexWithKatex(trimmed, displayMode)
}
